/**
 * The single entry point the rest of the app uses to reach the native shell.
 *
 *     import { bridge } from './native/bridge';
 *     bridge.startSpeech(['scan', 'fire']);
 *     bridge.on('speechCommand', e => …);
 *
 * `bridge` is a `NativeShell` (see ./types.ts) plus a small typed `on()` helper
 * for subscribing to individual event types. At import time we pick the best
 * available implementation:
 *
 *   1. window.__wildhunt_shell  – a shell that implements NativeShell directly
 *                                 (iOS, test harness, anything modern).
 *   2. window.WildHuntAndroid   – Android @JavascriptInterface (string-only),
 *                                 adapted below.
 *   3. PWA fallback             – best-effort using Web APIs.
 */
import type { NativeShell, NativeEvent, NativeEventType, ShellCapabilities, GeoFix } from './types';
import { createPwaShell } from './pwa';
import { harnessClientId } from './harnessEnv';
import { createHarnessShell } from './harness';

declare global {
  interface Window {
    __wildhunt_shell?: NativeShell;
    /** Android @JavascriptInterface object (string-only methods). */
    WildHuntAndroid?: AndroidInterface;
    /** Sink the Android shell calls to push events up as JSON strings. */
    __wildhuntNativeEmit?: (json: string) => void;
  }
}

/** Flat, string-only surface exposed by the Android @JavascriptInterface. */
interface AndroidInterface {
  capabilities(): string; // JSON ShellCapabilities
  startSpeech(wordsJson: string): void;
  stopSpeech(): void;
  setAudioPlaying(playing: boolean): void;
  getAudioDebug(): string;
  startLocation(): void;
  stopLocation(): void;
  setKeepAwake(on: boolean): void;
  /** JSON object of all persisted prefs — read synchronously at startup. */
  getStorage(): string;
  setItem(key: string, value: string): void;
  loadApp(url: string | null): void;
  showToast(message: string): void;
}

/** Adapts the flat Android interface to a NativeShell. */
function createAndroidShell(android: AndroidInterface): NativeShell {
  // Android's @JavascriptInterface is synchronous, so we can grab the whole
  // SharedPreferences snapshot up-front (no document-start injection needed).
  let storage: Record<string, string> = {};
  try {
    storage = JSON.parse(android.getStorage()) as Record<string, string>;
  } catch {
    /* keep empty */
  }
  const handlers = new Set<(e: NativeEvent) => void>();
  // Android pushes events by calling window.__wildhuntNativeEmit(json).
  window.__wildhuntNativeEmit = (json: string) => {
    let e: NativeEvent;
    try {
      e = JSON.parse(json) as NativeEvent;
    } catch {
      return;
    }
    for (const h of handlers) h(e);
  };
  return {
    capabilities: () => JSON.parse(android.capabilities()) as ShellCapabilities,
    subscribe(handler) {
      handlers.add(handler);
      return () => handlers.delete(handler);
    },
    startSpeech: (words) => android.startSpeech(JSON.stringify(words)),
    stopSpeech: () => android.stopSpeech(),
    setAudioPlaying: (playing) => android.setAudioPlaying(playing),
    getAudioDebug: () => android.getAudioDebug(),
    startLocation: () => android.startLocation(),
    stopLocation: () => android.stopLocation(),
    setKeepAwake: (on) => android.setKeepAwake(on),
    storage,
    setItem: (key, value) => android.setItem(key, value),
    loadApp: (url) => android.loadApp(url ?? null),
    showToast: (message) => android.showToast(message),
  };
}

function pickShell(): NativeShell {
  // Dev-only play-test wrapper: an iframe loaded as index.html?harness=<id>.
  const harnessId = harnessClientId();
  if (harnessId != null) return createHarnessShell(harnessId);
  if (window.__wildhunt_shell) return window.__wildhunt_shell;
  if (window.WildHuntAndroid) return createAndroidShell(window.WildHuntAndroid);
  return createPwaShell();
}

class Bridge implements NativeShell {
  private shell: NativeShell;
  private caps: ShellCapabilities;
  /**
   * Live, synchronous key/value cache. Seeded once from the shell's startup
   * snapshot; `setItem` updates it in place so `getItem` is always immediate and
   * never needs a native round-trip (see NativeShell.storage).
   */
  private storageCache: Record<string, string>;
  /** per-type handler sets for the ergonomic `on()` API */
  private typed = new Map<NativeEventType, Set<(e: NativeEvent) => void>>();

  constructor() {
    this.shell = pickShell();
    this.caps = this.shell.capabilities();
    this.storageCache = { ...this.shell.storage };
    this.shell.subscribe((e) => {
      const set = this.typed.get(e.type);
      if (set) for (const h of set) h(e);
    });
  }

  // ── Ergonomic, type-narrowed event subscription ────────────────────────
  on<T extends NativeEventType>(
    type: T,
    handler: (e: Extract<NativeEvent, { type: T }>) => void,
  ): () => void {
    let set = this.typed.get(type);
    if (!set) this.typed.set(type, (set = new Set()));
    const wrapped = handler as (e: NativeEvent) => void;
    set.add(wrapped);
    return () => set!.delete(wrapped);
  }

  // ── NativeShell passthrough ────────────────────────────────────────────
  capabilities(): ShellCapabilities {
    return this.caps;
  }
  subscribe(handler: (e: NativeEvent) => void) {
    return this.shell.subscribe(handler);
  }
  startSpeech(words: string[]) {
    if (this.caps.speech) this.shell.startSpeech(words);
  }
  stopSpeech() {
    if (this.caps.speech) this.shell.stopSpeech();
  }
  setAudioPlaying(playing: boolean) {
    if (this.caps.speech) this.shell.setAudioPlaying(playing);
  }
  getAudioDebug(): string {
    return this.shell.getAudioDebug();
  }
  startLocation() {
    if (this.caps.location) this.shell.startLocation();
  }
  stopLocation() {
    if (this.caps.location) this.shell.stopLocation();
  }
  setKeepAwake(on: boolean) {
    if (this.caps.keepAwake) this.shell.setKeepAwake(on);
  }
  /** The synchronous snapshot (NativeShell contract), backed by the live cache. */
  get storage(): Record<string, string> {
    return this.storageCache;
  }
  /** Read from the in-memory cache — always synchronous, never a native call. */
  getItem(key: string): string | null {
    return key in this.storageCache ? this.storageCache[key] : null;
  }
  setItem(key: string, value: string) {
    this.storageCache[key] = value;
    this.shell.setItem(key, value);
  }
  loadApp(url?: string) {
    this.shell.loadApp(url);
  }
  showToast(message: string) {
    this.shell.showToast(message);
  }
}

export const bridge = new Bridge();
export type { GeoFix, NativeEvent, ShellCapabilities };
